import { UiElement } from "../UiElement.js";
import * as formattingUtils from "../../map/popup/formattingUtils.js";

export { GalleryOverview };

/**
 * A collection of static methods offered as tools for extending the functionality of the image gallery
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
class GalleryOverviewUtils {
    /**
     * Extract data in multiple feature info responses based on keys
     * @param {Array} list - list containing multiple similarly structured dictionaries
     * @param {string} key1 - key that represents the first child in the dictionary
     * @param {string} key2 - key that represents the second child in the dictionary
     * @return {Array} List with all dictionary entries (values) based on key1 with key2 as child key
     */
    static extractFeatureInfoData(list, key1, key2) {
        return list.map((item) => {
            if (Array.isArray(item))
                item = item[0];
            return item[key1][key2]
        })
    }

    /**
     * Apply filters on a list
     * @param {Array} list - list
     * @param {boolean} removeDuplicates - specifies whether duplicates in the returned list should be removed
     * @param {boolean} removeUndefined - specifies whether the returned list might contain undefined values
     * @return {Array} Filtered list
     */
    static filterList(list, removeDuplicates, removeUndefined) {
        let shallowCopy = [...list];
        if (removeUndefined)
            shallowCopy = shallowCopy.filter(n => n)
        if (removeDuplicates)
            shallowCopy = [...new Set(shallowCopy)]
        return shallowCopy
    }
}

class DataOverview {
    constructor() {
        this.html = '';
    }

    include(id, text, title) {
        if (text)
            this.html += `<span class="gallery-description" id="${id}" title="${title}">${text}</span>`
    }
}

class IconOverview {
    constructor() {
        this.html = '';
    }

    include(el, key1, key2, className) {
        const removeDuplicates = true;
        const removeUndefined = false;
        const itemListTmp = GalleryOverviewUtils.extractFeatureInfoData(el.features, key1, key2);
        let itemList = GalleryOverviewUtils.filterList(itemListTmp, removeDuplicates, removeUndefined);

        const isIconInfoAlwaysDefined = itemList.every(item => (item != null));
        if (isIconInfoAlwaysDefined) {
            itemList.forEach((item) => {
                if (item) {
                    if (className == 'icon-image-capture-mode' && item == 'mixed')
                        item = 'timer-and-manual'

                    const capitalizeFirstLetter = (str, exceptions = ['and', 'or', 'nor', 'but', 'at', 'by', 'for', 'in', 'of', 'on', 'a', 'an', 'the']) => {
                        return str.replace(/\w*/g, (word) => {
                            if (exceptions.includes(word))
                                return word
                            return word.charAt(0).toUpperCase() + word.substr(1).toLowerCase()
                        })
                    }
                    const iconKey = capitalizeFirstLetter(className.replace('icon-', '').replaceAll('-', ' '));
                    let iconValue = item;
                    if (!className.toLowerCase().includes('license'))
                        iconValue = capitalizeFirstLetter(iconValue.replaceAll('-', ' '));
                    let title = `"${iconKey}: ${iconValue}"`
                    this.html += `<span class="${className + '_' + item.replace(' ', '-')}" title=${title}></span>`
                }
            })
        }
    }
}


class OverviewEvent {
    constructor(features) {
        this.features = features;
    }

    get text() {
        this._collect();
        return this.eventListGrouped.join(', ')
    }

    get title() {
        this._collect();
        return this.eventList.join(', ')
    }

    _collect() {
        if (!this.eventList) {
            const removeDuplicates = true;
            const removeUndefined = true;
            const eventList = GalleryOverviewUtils.extractFeatureInfoData(this.features, 'properties#harmonized', 'event');
            this.eventList = GalleryOverviewUtils.filterList(eventList, removeDuplicates, removeUndefined);
        }
        if (!this.eventListGrouped)
            this._group();
    }

    _group(maximum = 5) {
        const regexReductionSteps = [/^[A-Z]+\d+(?:[\_|\W])\d+/, /^[A-Z]+\d+/, /^[A-Z]+/];
        this.eventListGrouped = this.eventList;
        regexReductionSteps.forEach((regexpr) => {
            if (this.eventListGrouped.length > maximum) {
                this.eventListGrouped = this.eventListGrouped.reduce((prev, curr) => {
                    const matchResult = curr.match(regexpr);
                    let match;
                    if (!matchResult) {
                        console.error(`event matching failed for "${curr}" via regex: ${regexpr}`)
                        match = curr;
                    }
                    else {
                        match = matchResult[0]
                    }
                    if (!prev.includes(match))
                        prev.push(match)
                    return prev
                }, [])
            }
        })
    }

}

class OverviewAuthor {
    constructor(features) {
        this.features = features;
    }

    get text() {
        this._collect();
        return this._group(3)
    }

    get title() {
        this._collect();
        return this._group(Infinity)
    }

    _collect() {
        if (!this.authors || this.citedAuthors)
            this._getData();
        if (!this.authorList) {
            this.authors.forEach(this._matchAuthor.bind(this));
            this.citedAuthors.forEach(this._matchAuthor.bind(this));
        }
    }

    _getData() {
        const removeDuplicates = false;
        const removeUndefined = true;
        window.features = this.features;
        const authorListTmp = GalleryOverviewUtils.extractFeatureInfoData(this.features, 'properties#harmonized', 'pi_name');
        this.authors = GalleryOverviewUtils.filterList(authorListTmp, removeDuplicates, removeUndefined);
        const citationListTmp = GalleryOverviewUtils.extractFeatureInfoData(this.features, 'properties#harmonized', 'citation');
        const citations = GalleryOverviewUtils.filterList(citationListTmp, removeDuplicates, removeUndefined);

        const citationsCounted = {};
        citations.forEach((x) => { citationsCounted[x] = (citationsCounted[x] || 0) + 1; });

        this.citedAuthors = [];
        for (const citation in citationsCounted) {
            const authorsPattern = /.*?(?=(?:\ *?\(\d{4}\))?:)/;
            const authorsMatch = citation.match(authorsPattern)[0]
            const singleAuthorPattern = /(?:[\u00c0-\u01ffa-zA-Z'\-])+,(?:\ *(?:[\u00c0-\u01ffa-zA-Z'\-]))+/g;
            const authorList = [...authorsMatch.matchAll(singleAuthorPattern)];
            const authorListRepeated = Array(citationsCounted[citation]).fill(authorList).flat()
            this.citedAuthors = this.citedAuthors.concat(...authorListRepeated)
        }
    }

    _group(limit = 3) {
        if (this.authorList) {
            this.authorList.sort(function (a, b) {
                return ((a.count > b.count) ? -1 : ((a.count == b.count) ? 0 : 1));
            });

            return this.authorList.reduce((prev, curr, index) => {
                if (index == limit)
                    return prev + ' et al.'
                if (index > limit)
                    return prev
                return prev + `${index != 0 ? ';' : ''} ${curr['lastName']}, ${curr['firstName']}`
            }, '')
        }
        else
            return undefined
    }

    _matchAuthor(author) {
        const regexFirstNameLastName = /^(?:(?:[\u00c0-\u01ffa-zA-Z'\-\.]+)\ ?){2,}$/;
        const regexLastNameFirstName = /^(?:(?:[\u00c0-\u01ffa-zA-Z'\-\.]+)){1,},\ (?:(?:[\u00c0-\u01ffa-zA-Z'\-\.]+)\ ?){1,}$/;
        const matchFirstNameLastName = author.match(regexFirstNameLastName);
        const matchLastNameFirstName = author.match(regexLastNameFirstName);
        let firstName, lastName;
        if (!this.authorList)
            this.authorList = [];

        if (matchFirstNameLastName) {
            const tmp = matchFirstNameLastName[0].split(' ');
            [firstName, lastName] = [tmp.slice(0, -1).join(' '), tmp.slice(-1)[0]];
        }
        else if (matchLastNameFirstName) {
            [lastName, firstName] = matchLastNameFirstName[0].split(',');
        }
        else {
            console.log('Can not match regex expression with author: ', author);
        }

        if (firstName || lastName) {
            firstName = firstName.trim();
            lastName = lastName.trim();
            const checkAuthor = (item) => item['firstName'] == firstName && item['lastName'] == lastName;
            const isAuthorInList = this.authorList.some(checkAuthor);
            if (!isAuthorInList) {
                this.authorList.push({ 'firstName': firstName, 'lastName': lastName, 'count': 1 });
            }
            else {
                this.authorList.forEach((value, index) => {
                    if (checkAuthor(this.authorList[index]))
                        this.authorList[index]['count'] += 1;
                })

            }
        }
    }
}

class OverviewDepth {
    constructor(features) {
        this.features = features;
    }

    get text() {
        if (!this.depthList) {
            this.depthList = GalleryOverviewUtils.extractFeatureInfoData(this.features, 'properties#harmonized', 'depth');
        }
        const isDepthAlwaysDefined = this.depthList.every(item => (item != null));
        if (!this.depthText && isDepthAlwaysDefined) {
            const depth_min = this.depthList.some((item) => item != null) ? parseFloat(Math.min(...this.depthList).toPrecision(5)) : undefined;
            const depth_max = this.depthList.some((item) => item != null) ? parseFloat(Math.max(...this.depthList).toPrecision(5)) : undefined;
            if (depth_max || depth_min) {
                this.depthText = (depth_max != depth_min && depth_max && depth_min) ? `${depth_min} - ${depth_max} m` : `${depth_min || depth_max} m`;
            }
        }
        return this.depthText
    }

    get title() {
        return 'Depth'
    }
}

class OverviewCoordinate {
    constructor(features) {
        this.features = features;
        this.toggleCoordinates = true;
    }

    get text() {
        if (!this.coordinateText) {
            const coordinateList = GalleryOverviewUtils.extractFeatureInfoData(this.features, 'geometry', 'coordinates');

            const isDepthAlwaysDefined = coordinateList.every(item => (item != null));
            if (!isDepthAlwaysDefined)
                return null

            this.longitude = [];
            this.latitude = [];
            coordinateList.forEach((item, index) => {
                if (!(Array.isArray(item[0]))) { //Point
                    this.longitude.push(item[0]);
                    this.latitude.push(item[1]);
                } else { //Multipoint
                    let tmp1 = [];
                    let tmp2 = [];
                    item.forEach((subitem, subindex) => {
                        tmp1.push(subitem[0]);
                        tmp2.push(subitem[1]);
                    })
                    this.longitude.push(this._average(tmp1));
                    this.latitude.push(this._average(tmp2));
                }
            })
            this.coordinateText = this.formatCoordinateText();
        }
        return this.coordinateText
    }

    get title() {
        return 'Average Position'
    }

    _average(list) {
        return list.reduce((prev, curr) => prev + curr) / list.length
    }

    formatCoordinateText() {
        return `${formattingUtils.formatLatLng(this._average(this.latitude), this._average(this.longitude), (!this.toggleCoordinates) ? "degreeminutes" : null)}`;
    }
}

class OverviewDate {
    constructor(features) {
        this.features = features;
    }

    get text() {
        let dateTimeStartList = GalleryOverviewUtils.extractFeatureInfoData(this.features, 'properties#harmonized', 'date_time').sort();
        const isDateTimeStartAlwaysDefined = dateTimeStartList.every(item => (item != null));

        if (!isDateTimeStartAlwaysDefined)
            return null

        let dateTimeStartText = undefined;
        if (dateTimeStartList.some((item) => item != null)) {
            const dateOptions = {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            };
            let dateTimeStart_first = new Date(dateTimeStartList[0]).toLocaleDateString('en-US', dateOptions)
            let dateTimeStart_last = new Date(dateTimeStartList[dateTimeStartList.length - 1]).toLocaleDateString('en-US', dateOptions);
            dateTimeStartText = (dateTimeStart_first == dateTimeStart_last) ? dateTimeStart_first : `${dateTimeStart_first} - ${dateTimeStart_last}`;
        }
        return dateTimeStartText
    }

    get title() {
        return 'Event Date'
    }
}

class OverviewCount {
    constructor(features) {
        this.features = features;
    }

    get text() {
        const count = this.features.length;
        const countText = count != 1 ? `${count} Items` : `${count} Item`;
        return countText
    }

    get title() {
        return 'Number of Items'
    }
}

/**
 * Provides an overview of a media gallery, summarizing the most important information about the gallery
 * @memberof vef.ui.media
 * @extends UiElement
 * @author ckraemme <ckraemme@awi.de>
 */
class GalleryOverview extends UiElement {
    /**
     * Initialization of the overview: collecting and providing the information regarding the content of the media gallery
     * @param {string/HTMLElement} targetElement - target html element, serves as anchor for the media gallery overview
     * @param {Object} featureResponse - dictionary containing data information. Structure of the dictionary is based on the harmonized response of the maps.awi.de web feature service
     */
    constructor(targetElement, featureResponse) {
        super(targetElement);
        this.features = featureResponse;
        if (this.features.length > 0)
            this.provideInfo();
        else
            console.log('No data available to build gallery overview')
    }

    /**
     * Collect, prepare and visualize the media gallery content information
     */
    provideInfo() {

        // Step 1: Dataset description
        const event = new OverviewEvent(this.features);
        const author = new OverviewAuthor(this.features);
        const depth = new OverviewDepth(this.features);
        const coordinate = new OverviewCoordinate(this.features);
        const date = new OverviewDate(this.features);
        const count = new OverviewCount(this.features);

        const dataOverview = new DataOverview();
        dataOverview.include('author', author.text, author.title);
        dataOverview.include('depth', depth.text, depth.title);
        dataOverview.include('coordinate', coordinate.text, coordinate.title);
        dataOverview.include('date', date.text, date.title);
        dataOverview.include('dataset', count.text, count.title);

        // Step 2: Icons
        const iconOverview = new IconOverview();
        iconOverview.include(this, 'properties#harmonized', 'acquisition', 'icon-image-acquisition');
        iconOverview.include(this, 'properties#harmonized', 'marine-zone', 'icon-image-marine-zone');
        iconOverview.include(this, 'properties#harmonized', 'deployment', 'icon-image-deployment');
        iconOverview.include(this, 'properties#harmonized', 'navigation', 'icon-image-navigation');
        iconOverview.include(this, 'properties#harmonized', 'illumination', 'icon-image-illumination');
        iconOverview.include(this, 'properties#harmonized', 'scale-reference', 'icon-image-scale-reference');
        iconOverview.include(this, 'properties#harmonized', 'quality', 'icon-image-quality');
        iconOverview.include(this, 'properties#harmonized', 'pixel-magnitude', 'icon-image-resolution');
        iconOverview.include(this, 'properties#harmonized', 'spectral-resolution', 'icon-image-spectral-resolution');
        iconOverview.include(this, 'properties#harmonized', 'capture-mode', 'icon-image-capture-mode');
        iconOverview.include(this, 'properties#harmonized', 'license', 'icon-image-license');

        // Other icons that exist but their information is not yet in the dataset (because it is related to "iFDO content (optional)" section in SOP iFDO Creation v1.0):
        //iconOverview.include(this, 'properties#harmonized', '?', 'icon-image-annotation-creator'); 
        //iconOverview.include(this, 'properties#harmonized', '?', 'icon-image-annotation-geometry');

        // Step 3: Append to DOM
        const html = `
            <div id="gallery-header">
                <h1 title="${event.title}">${event.text}</h1>
                <div class="gallery-flex-container-icon">
                    ${iconOverview.html}
                </div>
                <div class="gallery-flex-container-data">
                    ${dataOverview.html}
                </div>
            </div>
        `;
        const element = this.getElement();
        element.classList.add("gallery-overview");
        element.insertAdjacentHTML("beforeend", html);

        // Step 4: Include events
        document.getElementById('coordinate').onclick = (el) => {
            coordinate.toggleCoordinates = !coordinate.toggleCoordinates;
            el.target.innerHTML = coordinate.formatCoordinateText();
        }
    }
}
